import classNames from 'classnames';
import {
  Txt,
  FlexContainer,
  View,
  Input,
  TitledBlock,
  Button,
  Spinner,
  ErrorMessage,
  Icon,
  SupportBlock,
} from 'components';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { usePublicAccount, useDataActions } from 'state';
import { useNavigationCallback } from 'hooks';
import styles from './PaymentsAddBankManualScreen.module.scss';

/**
 * PaymentsAddBankAccountScreen
 *
 * @return {JSX.Element}
 */
const PaymentsAddBankManualScreen = ({ className }) => {
  const navigate = useNavigate();
  const storageAction = useDataActions();
  const publicAccount = usePublicAccount();
  const bankingHasLinked = !!publicAccount.data.banking?.plaidRecipientId;
  const isEdit = bankingHasLinked;
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [dataState, setDataState] = useState(() => ({
    accountName: publicAccount.data.banking.accountName,
  }));

  const { callbackParam, gotToCallback } = useNavigationCallback();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await storageAction.general.addBankAccount(dataState);

      if (callbackParam) {
        gotToCallback(true);
      } else {
        navigate(-1);
      }
    } catch (e) {
      setError(e.message);
      setIsLoading(false);
    }
  };

  const handleChange = ({ target: { name, value } }) => {
    setDataState((c) => ({
      ...c,
      [name]: value,
    }));
  };

  const isDisabled = isLoading;

  return (
    <FlexContainer
      className={classNames(
        styles.PaymentsAddBankManualScreen,
        className,
        `${PaymentsAddBankManualScreen.displayName}`
      )}
      column
      gap
    >
      <Helmet title="Link a bank account" />
      <View className={styles.Header}>
        <Txt xxx>Fill in the details of your bank account below</Txt>
      </View>
      <View>
        <Txt regular>
          Your bank details are securely stored by our partner Plaid (Plaid
          Financial Ltd) who are an FCA authorised payments provider. More
          information on how Propa processes payments can be found here.
        </Txt>
      </View>
      <form onSubmit={handleSubmit}>
        <FlexContainer column gap>
          <FlexContainer column>
            <TitledBlock title="Account name">
              <Input
                fullWidth
                required
                name="accountName"
                placeholder="Mr J Appleseed"
                value={dataState.accountName || ''}
                onChange={handleChange}
                disabled={isDisabled}
                reverse
              />
            </TitledBlock>
            <TitledBlock title="Sort code">
              <Input
                fullWidth
                required
                minLength={6}
                maxLength={8}
                name="sortCode"
                // pattern={'(\\d{2}-?\\d{2}-?\\d{2})'}
                // title="The Sort code must be in a format 00-00-00"
                placeholder="000000"
                value={dataState.sortCode || ''}
                onChange={handleChange}
                disabled={isDisabled}
                reverse
              />
            </TitledBlock>
            <TitledBlock title="Account number">
              <Input
                fullWidth
                required
                pattern={'\\d{8}'}
                title="The Account number must have 8 digits"
                minLength={8}
                maxLength={8}
                name="accountNumber"
                placeholder="00000000"
                value={dataState.accountNumber || ''}
                onChange={handleChange}
                disabled={isDisabled}
                reverse
              />
            </TitledBlock>
            <View>
              <Txt regular>
                We store the last 4 digits of your account number so you have a
                reference to the account that is linked.
              </Txt>
            </View>
          </FlexContainer>
          {error ? <ErrorMessage title={error} /> : null}
          <FlexContainer>
            <Button flex type="submit" big success disabled={isDisabled}>
              {isLoading ? (
                <Spinner />
              ) : (
                <FlexContainer spaceBetween alignCenter flex>
                  <Txt xx>{isEdit ? 'Update' : 'Link'} this bank account</Txt>
                  <Icon name="goto" />
                </FlexContainer>
              )}
            </Button>
          </FlexContainer>
        </FlexContainer>
      </form>
      <SupportBlock />
    </FlexContainer>
  );
};

PaymentsAddBankManualScreen.displayName = 'PaymentsAddBankManualScreen';

export default observer(PaymentsAddBankManualScreen);
